import { isNull } from 'lodash'
import { normaliseDocumentNumber } from '../normalise-document-number'
import type { FamilyResult } from '../..'

/**
 * Check if input string is a substring or superset of any of the strings in the array.
 * If so, return said string from the array. Else return null.
 *
 * @param {string} input - The input string to check.
 * @param {Set<string>} strings - The set of strings to check against.
 * @returns {string | null} The matching string from the array or null if no match is found.
 */
function getMatchingString(input: string, strings: Set<string>): string | null {
  for (const string of strings) {
    if (input.includes(string) || string.includes(input)) {
      return string
    }
  }
  return null
}

/**
 * Processes and deduplicates priority claims by normalizing document numbers.
 *
 * This function filters out priority claims without a country, removes duplicates
 * based on normalized document numbers, and updates document numbers to include
 * country and kind information if missing. The resulting claims are returned
 * with updated document numbers.
 *
 * @param {FamilyResult['priorityClaims']} priorityClaims - The list of priority claims to process.
 * @returns {FamilyResult['priorityClaims']} An array of deduplicated and updated priority claims.
 */
export const processPriorityClaims = (
  priorityClaims: FamilyResult['priorityClaims']
) => {
  const deduplicatedClaims: FamilyResult['priorityClaims'] = []
  const seenDocuments = new Set<string>()
  const filteredPriorityClaims =
    priorityClaims?.filter((claim) => Boolean(claim.country)) ?? []

  for (const claim of filteredPriorityClaims) {
    const normalizedDocumentNumber = normaliseDocumentNumber(
      claim.documentNumber
    )

    const matchingDocumentNumber = getMatchingString(
      normalizedDocumentNumber,
      seenDocuments
    )

    if (
      isNull(matchingDocumentNumber) &&
      ((claim.country && claim.kind) || !/\.\d+$/.test(claim.documentNumber))
    ) {
      // Add unique and valid claims to the deduplicated list.
      deduplicatedClaims.push(claim)
      seenDocuments.add(normalizedDocumentNumber)
    }

    // Keep the longest document number only.
    if (
      !isNull(matchingDocumentNumber) &&
      matchingDocumentNumber.length < normalizedDocumentNumber.length
    ) {
      seenDocuments.add(normalizedDocumentNumber)
      seenDocuments.delete(matchingDocumentNumber)
      const claimToUpdate = deduplicatedClaims.find(
        (claim) => claim.documentNumber === matchingDocumentNumber
      )

      if (claimToUpdate) {
        claimToUpdate.documentNumber = normalizedDocumentNumber
      }
    }
  }

  return deduplicatedClaims.map((claim) => {
    const { documentNumber, country, kind } = claim
    let updatedDocumentNumber = normaliseDocumentNumber(documentNumber)

    if (country && !updatedDocumentNumber.startsWith(country)) {
      updatedDocumentNumber = `${country}${updatedDocumentNumber}`
    }

    if (kind && !updatedDocumentNumber.endsWith(kind)) {
      updatedDocumentNumber = `${updatedDocumentNumber}${kind}`
    }

    return {
      ...claim,
      documentNumber: updatedDocumentNumber,
    }
  })
}
